<template>
  <div
    class="fixed z-[99998] w-80 max-w-[90vw] flex flex-col items-end transition-[bottom] duration-300 ease-in-out"
    :class="[{ 'right-6': !isMobile }, { 'right-1': isMobile }]"
    :style="{ bottom: bottomPx + 'px' }"
    v-if="showWrapper"
    ref="wrapperRef"
  >
    <Transition name="fade">
      <div
        v-if="showCard"
        class="bg-white shadow-xl rounded-lg border border-border-secondary overflow-hidden"
      >
        <div class="bg-primary text-white p-4 gap-2">
          <div class="flex items-center justify-between">
            <div class="text-lg">{{ $t('Getting started') }}</div>
            <BaseButton
              :symbol="'close'"
              :type="'icon-secondary'"
              @click="close"
            ></BaseButton>
          </div>

          <div class="flex items-center gap-2">
            <div class="text-xs">{{ progressLabel }}</div>
            <div class="h-2 grow bg-desk-utility-brand-primary-700 rounded">
              <div
                class="h-2 bg-white rounded"
                :style="{ width: progressPct + '%' }"
              />
            </div>
          </div>
        </div>

        <ul class="gap-3 p-4 flex flex-col">
          <li
            v-for="item in items"
            :key="item.key"
            class="flex items-start gap-2"
          >
            <span class="flex items-start">
              <span
                v-if="item.done"
                class="mt-1 material-symbols-outlined text-icons-secondary"
                >check_circle</span
              >
              <span
                v-else
                class="mt-1 inline-block w-4 h-4 rounded-full border-2 border-icons-brand-primary"
              ></span>
            </span>
            <span
              :class="item.done ? 'line-through text-text-secondary' : ''"
              >{{ $t(item.label) }}</span
            >
          </li>
        </ul>
      </div>
    </Transition>

    <Transition name="app-fade">
      <div
        v-if="showPill"
        class="mt-3 p-4 w-fit gap-2 flex items-center rounded-2xl text-white shadow border border-border-secondary font-semibold transition-colors duration-500"
        :class="allDone ? 'bg-utility-signal-success-500' : 'bg-primary'"
      >
        <span class="material-symbols-outlined transition-transform"
          >check_circle</span
        >
        <span>{{ $t('Getting started') }}: {{ completed }}/5</span>
      </div>
    </Transition>
  </div>
  <!-- <BaseButton
    @click="pillClosed = !pillClosed"
    :label="'pillClosed'"
  ></BaseButton>
  <BaseButton @click="testDone = !testDone" :label="'testDone'"></BaseButton> -->
</template>

<script setup lang="ts">
const { $bus, $features } = useNuxtApp()
const configStore = useConfig()
const route = useRoute()
const cardClosed = ref(false)
const pillClosed = ref(false)
const isInEditor = computed(() => route.path.includes('edit'))
// const testDone = ref(false)

const tasks = computed(
  () => configStore.config?.onboardingTours?.deskTasks || {}
)

const items = computed(() => [
  { key: 'account', label: 'Create your account', done: true },
  {
    key: 'collection',
    label: 'Create your first collection',
    done: true,
  },
  {
    key: 'upload',
    label: 'Upload your first document',
    done: tasks.value.upload,
  },
  {
    key: 'recognition',
    label: 'Recognise your first document',
    done: tasks.value.recognition,
  },
  {
    key: 'edit',
    label: 'Make edits to the document text and save them',
    done: tasks.value.edit,
  },
])

const currentStep = ref(0)
const completed = computed(() => items.value.filter(i => i.done).length)
// const allDone = computed(
//   () => completed.value === items.value.length || testDone.value
// )
const allDone = computed(() => completed.value === items.value.length)
const progressPct = computed(() =>
  Math.round((completed.value / items.value.length) * 100)
)
const progressLabel = computed(
  () => `${completed.value} / ${items.value.length}`
)

const showWrapper = computed(() => {
  if (!configStore?.config) return false
  return showCard.value || !pillClosed.value
})

watch(allDone, done => {
  if (done) {
    setTimeout(() => {
      pillClosed.value = true
    }, 1500)
  }
})

const showPill = computed(() => {
  if (!$features?.setupTour) return false
  return !pillClosed.value
})

const showCard = computed(() => {
  if (!$features?.setupTour) return false
  if (cardClosed.value) return false
  return !allDone.value
})

onMounted(async () => {
  $bus.on('onboarding:document-uploaded', () => updateTask('upload', true))
  $bus.on('onboarding:recognition-started', recognitionNextStep)
  $bus.on('onboarding:edit-saved', () => updateTask('edit', true))
  $bus.on('onboarding:step-changed', stepChanged)
  $bus.on('startOnboarding', open)
  if (!configStore.config) {
    await configStore.fetchConfig()
  }
  pillClosed.value = configStore.config?.onboardingTours?.deskCompleted
  cardClosed.value = configStore.config?.onboardingTours?.deskCompleted
  window.addEventListener('resize', handleResize)
  nextTick(() => recalcBottom())
})

onUnmounted(() => {
  $bus.off('onboarding:document-uploaded')
  $bus.off('onboarding:recognition-started')
  $bus.off('onboarding:edit-saved')
  $bus.off('startOnboarding')
  window.removeEventListener('resize', handleResize)
})

function stepChanged(index: number) {
  currentStep.value = index
}

async function recognitionNextStep() {
  updateTask('recognition', true)
  $bus.emit('onboarding:step-next', 2)
}

async function updateTask(
  key: 'upload' | 'recognition' | 'edit',
  value: boolean
) {
  const currentTours = configStore.config?.onboardingTours || {}
  const currentTasks = currentTours.deskTasks || {}

  // avoid sending request if nothing changed
  if (currentTasks[key] === value) return

  const updated = { ...currentTasks, [key]: value }
  // If all tasks are finished, also mark deskCompleted true
  const allFinished = !!(updated.upload && updated.recognition && updated.edit)

  if ($features.setupTour) {
    await configStore.updateOnBoardingTours({
      ...currentTours,
      deskTasks: updated,
      deskCompleted: allFinished ? true : currentTours.deskCompleted,
    })
  }
}

async function close() {
  cardClosed.value = true
  pillClosed.value = true
  await configStore.updateOnBoardingTours({
    ...configStore.config?.onboardingTours,
    deskCompleted: true,
  })
}

function open() {
  const allTasksfinished = !!(
    configStore.config?.onboardingTours?.deskTasks?.upload &&
    configStore.config?.onboardingTours?.deskTasks?.recognition &&
    configStore.config?.onboardingTours?.deskTasks?.edit
  )
  if (allTasksfinished) {
    cardClosed.value = true
    pillClosed.value = true
    return
  }
  cardClosed.value = false
  pillClosed.value = false
}

const windowWidth = ref(window.innerWidth) // Necessary to handle isMobileCheck

const isMobile = computed(() => {
  return windowWidth.value < 1024
})

// Smooth bottom positioning driven by currentStep and isInEditor
const wrapperRef = ref<HTMLElement | null>(null)
const bottomPx = ref(24) // corresponds to bottom-6

const wantsTop = computed(() => currentStep.value > 1 && !isInEditor.value)

function recalcBottom() {
  const el = wrapperRef.value
  if (!el) return
  const margin = 24 // 1.5rem (tailwind 6)
  const vh = window.innerHeight
  const h = el.offsetHeight
  if (wantsTop.value) {
    // When we want the wrapper near the top (previously top = margin), compute equivalent bottom
    bottomPx.value = Math.max(0, vh - h - margin)
  } else {
    // When we want it near the bottom
    bottomPx.value = margin
  }
}

function handleResize() {
  windowWidth.value = window.innerWidth
  nextTick(() => recalcBottom())
}

watch([wantsTop, () => showCard.value, () => showPill.value], () => {
  nextTick(() => recalcBottom())
})
</script>
